import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import ApiKeyDialogContent from "@/components/PageHeader/actions/dialogs/integrations/ApiKeyDialogContent";
import ConfirmDeactivateResource from "@/components/PageHeader/actions/dialogs/reusableDialogs/ConfirmDeactivateResource/ConfirmDeactivateResource";
import { ALERT_STATUS } from "@/context/alert/types";
import { useApiCredentialValidation } from "@/Pages/Integrations/IntegrationsApi/hooks/useApiCredentialValidation";
import {
  API_KEY_FIELD_NAMES,
  ApiKeyObjectState
} from "@/Pages/Integrations/types";
import { ApiCredential, ROUTES, useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useRefreshTableData from "@/shared/hooks/useRefreshTableData";
import useResponse from "@/shared/hooks/useResponse";

interface EditApiKeyDialogProps {
  rowData: ApiCredential;
  isOpen: boolean;
  onClose: () => void;
}

const EditApiKeyDialog = ({
  rowData,
  isOpen,
  onClose
}: EditApiKeyDialogProps) => {
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();

  const { putData } = useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { refreshTableData } = useRefreshTableData();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [hasUpdatedEnable, setHasUpdatedEnable] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);

  const initialApiKeyObject: ApiKeyObjectState = {
    selectedCompany: {
      id: rowData.companyId,
      name: rowData.company
    },
    name: rowData.name,
    expirationDate: rowData.expirationDate,
    enabled: rowData.enabled
  };

  const {
    errors,
    setErrors,
    apiKeyObject,
    setApiKeyObject,
    isReadyToConfirm,
    resetAllValues
  } = useApiCredentialValidation(initialApiKeyObject, setHasUpdatedEnable);

  const handleEditSubmit = async (skipConfirm?: boolean) => {
    const isApiKeyDeactivateIdentical =
      initialApiKeyObject[API_KEY_FIELD_NAMES.Enabled] ===
      apiKeyObject[API_KEY_FIELD_NAMES.Enabled];

    if (
      !skipConfirm &&
      !isApiKeyDeactivateIdentical &&
      hasUpdatedEnable &&
      !apiKeyObject[API_KEY_FIELD_NAMES.Enabled]
    ) {
      setIsConfirmDeactivateOpen(true);
      return;
    }
    try {
      setIsLoading(true);

      const payload = {
        id: rowData.id,
        name: apiKeyObject.name,
        enabled: apiKeyObject.enabled
      };

      await putData(ApiResources.IntegrationsApiCredentials, payload);

      refreshTableData();

      handleResponse(
        ALERT_STATUS.Success,
        t("Dialog##api key edit##success banner")
      );

      onClose();
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsLoading(false);
  };

  return (
    <>
      <Dialog
        data-testid="edit-api-credential-dialog"
        title={t("Dialog##api key edit##dialog title", {
          keyName: rowData.name
        })}
        isTitleSeparator
        isActionsSeparator
        extendToMaxWidth={toMd}
        open={isOpen}
        close={onClose}
        submit={handleEditSubmit}
        width={toMd ? undefined : DialogWidth.MinWidth}
        slotProps={{ transition: { onExited: resetAllValues } }}
        actions={
          <DialogActions
            onClose={onClose}
            isLoading={isLoading}
            isreadyToConfirm={isReadyToConfirm}
            confirmationButtonText={t("Button##save")}
            confirmationButtonTestId="edit-api-credential-button"
            cancelButtonText={t("Button##close")}
            cancelButtonTestId="close-edit-api-credential"
          />
        }
      >
        <ApiKeyDialogContent
          isLoading={isLoading}
          apiKeyObject={apiKeyObject}
          errors={errors}
          setErrors={setErrors}
          setApiKeyObject={setApiKeyObject}
          readOnlyCompanyField
          hasUpdatedEnable={hasUpdatedEnable}
          setHasUpdatedEnable={setHasUpdatedEnable}
        />
      </Dialog>
      <ConfirmDeactivateResource
        isOpen={isConfirmDeactivateOpen}
        route={ROUTES.IntegrationsApi}
        resourceName={apiKeyObject[API_KEY_FIELD_NAMES.Name]}
        onClose={() => setIsConfirmDeactivateOpen(false)}
        onSubmit={() => handleEditSubmit(true)}
      />
    </>
  );
};

export default EditApiKeyDialog;
